import React, { useState } from 'react';
import { Box, Button, Typography, IconButton, InputBase, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DashboardIcon from '@material-ui/icons/Dashboard';
import LockIcon from '@material-ui/icons/Lock';
import EmailIcon from '@material-ui/icons/Email';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes shake': {
      '0%, 100%': { transform: 'translateX(0)' },
      '20%': { transform: 'translateX(-8px)' },
      '40%': { transform: 'translateX(8px)' },
      '60%': { transform: 'translateX(-5px)' },
      '80%': { transform: 'translateX(5px)' },
    },
    '@keyframes float': {
      '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
      '33%': { transform: 'translateY(-12px) rotate(1deg)' },
      '66%': { transform: 'translateY(6px) rotate(-1deg)' },
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
      '50%': { opacity: 0.7, transform: 'scale(1.05)' },
    },
    '@keyframes slideUp': {
      '0%': { opacity: 0, transform: 'translateY(30px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
    '@keyframes gradientShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
  },
  root: {
    width: '100vw',
    height: '100vh',
    background: 'var(--adm-bg, #f4f5f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  orb1: {
    position: 'absolute',
    width: 480,
    height: 480,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(7,150,204,0.12) 0%, transparent 68%)',
    top: -140,
    right: -140,
    filter: 'blur(10px)',
    animation: '$pulse 8s ease-in-out infinite',
  },
  orb2: {
    position: 'absolute',
    width: 560,
    height: 560,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(16,167,116,0.11) 0%, transparent 68%)',
    bottom: -180,
    left: -180,
    filter: 'blur(10px)',
    animation: '$pulse 10s ease-in-out infinite 2s',
  },
  orb3: { display: 'none' },
  card: {
    width: 400,
    background: 'var(--adm-card, #ffffff)',
    border: '1px solid var(--adm-card-border, #eef0f2)',
    borderRadius: 20,
    padding: '44px 40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 18px 50px rgba(52,71,103,0.12)',
    animation: '$slideUp 0.6s ease-out',
  },
  logoWrap: {
    width: 68,
    height: 68,
    background: 'linear-gradient(135deg, #58BAE6 0%, #18BDEE 50%, #2FD596 100%)',
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 12px 30px rgba(7,150,204,0.35)',
    marginBottom: 4,
    position: 'relative',
  },
  logoPulse: {
    position: 'absolute',
    inset: -4,
    borderRadius: 24,
    border: '2px solid rgba(7,150,204,0.22)',
    animation: '$pulse 3s ease-in-out infinite',
  },
  title: {
    fontWeight: 800,
    fontSize: '1.55rem',
    color: 'var(--adm-text, #2A3547)',
    textAlign: 'center',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: '0.76rem',
    color: 'var(--adm-text-muted, #8a94a6)',
    textAlign: 'center',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginTop: -16,
  },
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: 16 },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 6, width: '100%' },
  fieldLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--adm-text-dim, #5a6a7e)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  fieldBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--adm-input-bg, #f8f9fb)',
    border: '1.5px solid var(--adm-input-border, #e6e9ee)',
    borderRadius: 12,
    padding: '0 14px',
    height: 48,
    transition: 'all 0.2s ease',
    '&:focus-within': {
      borderColor: 'var(--adm-brand, #2f5d50)',
      boxShadow: '0 0 0 4px var(--adm-brand-soft, rgba(47,93,80,0.08))',
      background: 'var(--adm-input-bg, #ffffff)',
    },
    '&:hover': { borderColor: 'var(--adm-input-border, #d3d8e0)' },
  },
  fieldIcon: { color: 'var(--adm-text-muted, #94a3b8)', fontSize: 18, flexShrink: 0 },
  fieldInput: {
    flex: 1,
    fontSize: '0.9rem',
    color: 'var(--adm-text, #2A3547)',
    '& input': {
      padding: 0,
      color: 'var(--adm-text, #2A3547)',
      '&::placeholder': { color: 'var(--adm-text-muted, #a0aec0)' },
    },
  },
  loginBtn: {
    background: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
    color: '#03121c',
    fontWeight: 700,
    fontSize: '0.9rem',
    padding: '13px 0',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(7,150,204,0.35)',
    marginTop: 8,
    letterSpacing: '0.02em',
    '&:hover': {
      background: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
      filter: 'brightness(1.06)',
      boxShadow: '0 12px 30px rgba(7,150,204,0.5)',
      transform: 'translateY(-1px)',
    },
    '&:active': { transform: 'translateY(0)' },
    transition: 'all 0.25s ease',
  },
  error: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 10,
    padding: '10px 14px',
    color: '#f87171',
    fontSize: '0.78rem',
    textAlign: 'center',
    width: '100%',
  },
  footer: {
    fontSize: '0.62rem',
    color: 'var(--adm-text-dim, #475569)',
    textAlign: 'center',
    letterSpacing: '0.04em',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  divider: {
    width: '100%',
    height: 1,
    background: 'linear-gradient(90deg, transparent, var(--adm-border, #eef0f2), transparent)',
    marginTop: -8,
  },
}));

function AdminLogin({ onLogin }) {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await onLogin(email, password);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.orb1} />
      <Box className={classes.orb2} />
      <Box className={classes.orb3} />

      <Box className={classes.card} style={{ animation: shaking ? 'shake 0.4s ease' : '$slideUp 0.6s ease-out' }}>
        <Box className={classes.logoWrap}>
          <Box className={classes.logoPulse} />
          <DashboardIcon style={{ color: '#03121c', fontSize: 34 }} />
        </Box>
        <Typography className={classes.title}>EcoPlanner Admin</Typography>
        <Typography className={classes.subtitle}>Painel de Administracao</Typography>

        <Box className={classes.divider} />

        <form className={classes.form} onSubmit={handleSubmit}>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>Email</Typography>
            <Box className={classes.fieldBox}>
              <EmailIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder="admin@ecoplanner.com" value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }} autoComplete="email" autoFocus />
            </Box>
          </Box>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>Palavra-passe</Typography>
            <Box className={classes.fieldBox}>
              <LockIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder="Introduza a palavra-passe"
                type={showPass ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }} autoComplete="current-password" />
              <IconButton size="small" onClick={() => setShowPass(v => !v)} style={{ color: 'var(--adm-text-dim, #64748b)', padding: 4 }}>
                {showPass ? <VisibilityOffIcon style={{ fontSize: 16 }} /> : <VisibilityIcon style={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
          </Box>
          {error && <Box className={classes.error}>{error}</Box>}
          <Button type="submit" fullWidth variant="contained" className={classes.loginBtn} disableElevation disabled={loading}>
            {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Entrar'}
          </Button>
        </form>
        <Typography className={classes.footer}>
          <SecurityIcon style={{ fontSize: 12, color: 'var(--adm-text-dim, #475569)' }} />
          Acesso restrito a administradores
        </Typography>
      </Box>
    </Box>
  );
}

export default AdminLogin;
